// ---------------------------------------------------------------------------
// Training Academy progress store — persisted per admin realm in localStorage
// so an administrator can close the tab and resume exactly where they left off.
// ---------------------------------------------------------------------------

import type { AdminRealm } from '@/lib/adminAuth';
import type { QuizAttempt, TrainingProgress } from '@/data/trainingTypes';

const keyFor = (realm: AdminRealm) => `zera_training_${realm}`;

export function emptyProgress(): TrainingProgress {
  return {
    visited: {},
    completedSections: [],
    skippedSections: [],
    bookmarks: [],
    lastPosition: null,
    timeSpentSec: 0,
    attempts: [],
    certified: false,
  };
}

export function loadProgress(realm: AdminRealm): TrainingProgress {
  try {
    const raw = localStorage.getItem(keyFor(realm));
    if (!raw) return emptyProgress();
    const parsed = JSON.parse(raw);
    return { ...emptyProgress(), ...parsed };
  } catch {
    return emptyProgress();
  }
}

export function saveProgress(realm: AdminRealm, p: TrainingProgress): void {
  try {
    localStorage.setItem(keyFor(realm), JSON.stringify(p));
  } catch {
    /* storage full / private mode — ignore */
  }
}

function update(realm: AdminRealm, fn: (p: TrainingProgress) => TrainingProgress): TrainingProgress {
  const next = fn(loadProgress(realm));
  saveProgress(realm, next);
  return next;
}

export function markSlideVisited(realm: AdminRealm, slideId: string): TrainingProgress {
  return update(realm, (p) => ({ ...p, visited: { ...p.visited, [slideId]: true } }));
}

export function setLastPosition(realm: AdminRealm, sectionIdx: number, slideIdx: number): TrainingProgress {
  return update(realm, (p) => ({ ...p, lastPosition: { sectionIdx, slideIdx } }));
}

export function toggleBookmark(realm: AdminRealm, slideId: string): TrainingProgress {
  return update(realm, (p) => ({
    ...p,
    bookmarks: p.bookmarks.includes(slideId)
      ? p.bookmarks.filter((b) => b !== slideId)
      : [...p.bookmarks, slideId],
  }));
}

export function markSectionComplete(realm: AdminRealm, sectionId: string): TrainingProgress {
  return update(realm, (p) => ({
    ...p,
    completedSections: p.completedSections.includes(sectionId)
      ? p.completedSections
      : [...p.completedSections, sectionId],
    skippedSections: p.skippedSections.filter((s) => s !== sectionId),
  }));
}

export function markSectionSkipped(realm: AdminRealm, sectionId: string): TrainingProgress {
  return update(realm, (p) => ({
    ...p,
    skippedSections:
      p.skippedSections.includes(sectionId) || p.completedSections.includes(sectionId)
        ? p.skippedSections
        : [...p.skippedSections, sectionId],
  }));
}

export function addTimeSpent(realm: AdminRealm, seconds: number): TrainingProgress {
  const safe = Math.max(0, Math.min(seconds, 60 * 30)); // cap wild deltas
  return update(realm, (p) => ({ ...p, timeSpentSec: p.timeSpentSec + safe }));
}

export function recordQuizAttempt(realm: AdminRealm, attempt: QuizAttempt, name?: string): TrainingProgress {
  return update(realm, (p) => {
    const certified = p.certified || attempt.passed;
    return {
      ...p,
      attempts: [...p.attempts, attempt],
      certified,
      certifiedAt: certified ? p.certifiedAt || attempt.dateISO : undefined,
      certifiedName: name || p.certifiedName,
    };
  });
}

export function setCertifiedName(realm: AdminRealm, name: string): TrainingProgress {
  return update(realm, (p) => ({ ...p, certifiedName: name }));
}

export function resetProgress(realm: AdminRealm): TrainingProgress {
  const fresh = emptyProgress();
  saveProgress(realm, fresh);
  return fresh;
}

// ---------------------------------------------------------------------------
// Derived reporting helpers
// ---------------------------------------------------------------------------

export interface TrainingStats {
  completionPct: number;
  slidesVisited: number;
  totalSlides: number;
  attemptCount: number;
  bestScore: number;
  avgScore: number;
  lastAttempt: QuizAttempt | null;
  mostMissedTopics: { topicLabel: string; missed: number }[];
  timeSpentLabel: string;
  remainingMinutes: number;
}

export function computeStats(
  p: TrainingProgress,
  totalSlides: number,
  totalMinutes: number,
): TrainingStats {
  const slidesVisited = Object.keys(p.visited).length;
  const completionPct = totalSlides ? Math.min(100, Math.round((slidesVisited / totalSlides) * 100)) : 0;
  const scores = p.attempts.map((a) => a.scorePct);
  const missed: Record<string, number> = {};
  p.attempts.forEach((a) =>
    a.answers.forEach((ans) => {
      if (!ans.wasCorrect) missed[ans.topicLabel] = (missed[ans.topicLabel] || 0) + 1;
    }),
  );
  const mostMissedTopics = Object.entries(missed)
    .map(([topicLabel, count]) => ({ topicLabel, missed: count }))
    .sort((a, b) => b.missed - a.missed)
    .slice(0, 5);

  const mins = Math.round(p.timeSpentSec / 60);
  const timeSpentLabel = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;

  return {
    completionPct,
    slidesVisited,
    totalSlides,
    attemptCount: p.attempts.length,
    bestScore: scores.length ? Math.max(...scores) : 0,
    avgScore: scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0,
    lastAttempt: p.attempts.length ? p.attempts[p.attempts.length - 1] : null,
    mostMissedTopics,
    timeSpentLabel,
    remainingMinutes: Math.max(0, Math.round(totalMinutes * (1 - completionPct / 100))),
  };
}

/** Fisher–Yates shuffle (pure — returns a new array). */
export function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
